/* eslint-disable */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, timer } from 'rxjs';
import { webSocket, WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';

import { environment } from 'environments/environment';

interface HandshakeRequestPayload {
  connection_id: string;
  creator_id: string;
}

interface WebSocketMessage {
  action?: string;
  payload?: any;
  cid?: string;
  data?: unknown;
}

interface WebSocketConnection {
  socket: WebSocketSubject<WebSocketMessage>;
  connectionId: string;
  isConnected: boolean;
  lastPing: number;
}

interface PingMessage {
  action: 'ping';
}

interface WebSocketError {
  message: string;
  code?: number;
  type?: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {
  private wsServer: string = environment.wssBaseUrl;
  public wsConnectionId$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public wsConnection: WebSocketConnection | null = null;
  private pingInterval: Subscription | null = null;
  private messageSubject = new Subject<unknown>();
  private reconnectAttempts: number = 0;
  private readonly pingMessage: PingMessage = { action: 'ping' };
  private readonly maxReconnectAttempts: number = 5;
  private readonly reconnectBaseDelayMs: number = 1000;
  private readonly reconnectMaxDelayMs: number = 30000;
  private readonly pingIntervalMs: number = 30000;
  private readonly sendRetryDelayMs: number = 1000;

  public isConnectionBlocked(): boolean {
    const attempts = this.reconnectAttempts ?? 0;
    return attempts >= this.maxReconnectAttempts;
  }

  public resetReconnectAttempts(): void {
    this.reconnectAttempts = 0;
  }

  public connect(sessionToken: string, isPersistent: boolean = false): void {
    try {
      const socket = this._createWebSocketSubject(sessionToken, isPersistent);
      this._subscribeToSocketEvents(socket, isPersistent);

      this.wsConnection = {
        socket,
        connectionId: '',
        isConnected: true,
        lastPing: Date.now()
      };
    } catch (error) {
      console.error('Error creating WebSocket connection', error);
    }
  }

  private _createWebSocketSubject(
    sessionToken: string,
    isPersistent: boolean
  ): WebSocketSubject<WebSocketMessage> {
    const wsConnectionUrl = `${this.wsServer}?BearerToken=${encodeURIComponent(sessionToken)}`;

    const config: WebSocketSubjectConfig<WebSocketMessage> = {
      url: wsConnectionUrl,
      serializer: (value: WebSocketMessage) => JSON.stringify(value),
      deserializer: (event: MessageEvent) => this._processIncomingMessage(event),
      openObserver: {
        next: () => this._handleWebSocketOpen()
      },
      closeObserver: {
        next: () => this._handleWebSocketClose(isPersistent)
      }
    };

    return webSocket(config);
  }

  private _subscribeToSocketEvents(
    socket: WebSocketSubject<WebSocketMessage>,
    isPersistent: boolean
  ): void {
    socket.subscribe({
      // Messages handled by deserializer and piped to messageSubject
      error: (err: WebSocketError) => this._handleWebSocketError(isPersistent, err),
      complete: () => this._updateConnectionStatus(false)
    });
  }

  private _processIncomingMessage(event: MessageEvent): WebSocketMessage {
    try {
      const msg: WebSocketMessage = JSON.parse(event.data);
      if (msg.cid) {
        this.wsConnectionId$.next(msg.cid);
        if (this.wsConnection) {
          this.wsConnection.connectionId = msg.cid;
        }
      }

      const responseData = msg.data;
      if (!responseData) return msg;

      this.messageSubject.next(responseData);
      return msg;
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
      // Return a minimal WebSocketMessage object when parsing fails
      return { action: 'error', data: event.data };
    }
  }

  private _handleWebSocketOpen(): void {
    if (!this.wsConnection) return;

    this._updateConnectionStatus(true);
    this.resetReconnectAttempts(); // Reset attempts on successful connection
    this.startPing(this.wsConnection.socket);

    if (this.wsConnection.connectionId) {
      this.wsConnectionId$.next(this.wsConnection.connectionId);
    }
  }

  private _handleWebSocketClose(isPersistent: boolean): void {
    this.stopPing();
    this._updateConnectionStatus(false);

    if (this.isConnectionBlocked()) return;

    if (isPersistent) {
      this._scheduleReconnection();
    }
  }

  private _handleWebSocketError(isPersistent: boolean, error: WebSocketError): void {
    this.stopPing();
    this._updateConnectionStatus(false);

    if (this.isConnectionBlocked()) return;

    if (isPersistent) {
      this._scheduleReconnection();
    }
  }

  private _scheduleReconnection(): void {
    const attempts = (this.reconnectAttempts ?? 0) + 1;
    if (attempts > this.maxReconnectAttempts) return;

    this.reconnectAttempts = attempts;

    const delay = Math.min(
      this.reconnectBaseDelayMs * Math.pow(2, attempts - 1),
      this.reconnectMaxDelayMs
    );

    setTimeout(() => {
      const storedToken = localStorage.getItem('accessToken');
      if (storedToken && !this.isConnectionBlocked()) {
        this.connect(storedToken, true);
      }
    }, delay);
  }

  private _updateConnectionStatus(isConnected: boolean): void {
    if (this.wsConnection) {
      this.wsConnection.isConnected = isConnected;
    }
    if (!isConnected) {
      this.wsConnectionId$.next('');
    }
  }

  private startPing(socket: WebSocketSubject<WebSocketMessage>): void {
    this.stopPing(); // Stop any existing ping interval

    this.pingInterval = timer(0, this.pingIntervalMs).subscribe(() => {
      if (!this.wsConnection?.isConnected) {
        this.stopPing();
        return;
      }
      socket.next(this.pingMessage);
      this.wsConnection.lastPing = Date.now();
    });
  }

  private stopPing(): void {
    if (this.pingInterval) {
      this.pingInterval.unsubscribe();
      this.pingInterval = null;
    }
  }

  public sendAction(action: string, payload: any): void {
    const creatorId = payload['creator_id'] as string;
    if (!creatorId) return;

    const message: WebSocketMessage = { action, payload };

    if (this.wsConnection?.isConnected) {
      this.wsConnection.socket.next(message);
    } else {
      this._handleSendOnClosedConnection(message);
    }
  }

  public handshake(creatorId: string): void {
    if (!this.wsConnection?.isConnected) {
      return;
    }

    const handshakePayload: HandshakeRequestPayload = {
      connection_id: this.wsConnection?.connectionId,
      creator_id: creatorId
    };

    const message: WebSocketMessage = {
      action: 'HANDSHAKE',
      payload: handshakePayload
    };

    this.wsConnection.socket.next(message);

    // Send hidden "hello" message to trigger LLM after handshake
    this.sendHiddenHelloMessage(creatorId);
  }

  private sendHiddenHelloMessage(creatorId: string): void {
    if (!this.wsConnection?.isConnected) {
      return;
    }

    const helloPayload = {
      message: 'hello',
      creator_id: creatorId,
      hidden: true // Flag to indicate this is a hidden message
    };

    const helloMessage: WebSocketMessage = {
      action: 'MESSAGE',
      payload: helloPayload
    };

    this.wsConnection.socket.next(helloMessage);
  }

  private _handleSendOnClosedConnection(message: WebSocketMessage): void {
    const storedToken = localStorage.getItem('accessToken');

    if (!storedToken || this.isConnectionBlocked()) return;

    this.connect(storedToken, true);

    // Set send a message after a delay, checking if the connection is re-established
    setTimeout(() => {
      if (this.wsConnection?.isConnected) {
        this.wsConnection.socket.next(message);
      }
    }, this.sendRetryDelayMs);
  }

  public onMessage(): Observable<any> {
    return this.messageSubject.asObservable();
  }

  public isWebSocketConnected(): boolean {
    return this.wsConnection?.isConnected || false;
  }

  public closeConnection(): void {
    this.wsConnectionId$.next('');
    if (this.wsConnection?.socket) {
      this.wsConnection.socket.complete();
    }
    if (this.wsConnection) {
      this.wsConnection.isConnected = false;
    }
    this.stopPing();
    this.reconnectAttempts = this.maxReconnectAttempts;
  }

  public cleanup(): void {
    this.closeConnection();
    this.stopPing();
    this.reconnectAttempts = 0;
    this.messageSubject.complete();
    this.messageSubject = new Subject<unknown>();
  }
}
